import React, { useState, useEffect } from 'react';
import { useParams } from '../utils/SimpleRouter';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Lock } from 'lucide-react';
import { getWeatherIcon } from '../components/WeatherIcons';
import { getApiUrl, getApiHeaders, getBackendUrl } from '../utils/apiUrl';

const API = getApiUrl();
const BACKEND_URL = getBackendUrl();

const DisplayScreen = () => {
  const { qrCodeId } = useParams();
  const [data, setData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
  const [currentMessagePage, setCurrentMessagePage] = useState(0);
  const [weather, setWeather] = useState(null);
  const [settingsVersion, setSettingsVersion] = useState(null);
  const [contentVersion, setContentVersion] = useState(null);
  const [messagesPerPage, setMessagesPerPage] = useState(8); // Increased to 8 for better space utilization
  
  // Demo expired state
  const [demoExpired, setDemoExpired] = useState(false);
  const [expiredOrgName, setExpiredOrgName] = useState('');
  
  // Poster states
  const [posters, setPosters] = useState([]); // Now stores playlists with posters
  const [showingPoster, setShowingPoster] = useState(false);
  const [currentPoster, setCurrentPoster] = useState(null); // The actual poster object being displayed
  const [currentPosterIndex, setCurrentPosterIndex] = useState(0);
  const [posterFadeIn, setPosterFadeIn] = useState(false);
  
  // Screensaver states
  const [showingScreensaver, setShowingScreensaver] = useState(false);
  const [clockPosition, setClockPosition] = useState({ x: 100, y: 100 });
  const [clockVelocity, setClockVelocity] = useState({ x: 2, y: 2 });
  
  // iFrame reload state
  const [iframeReloadKey, setIframeReloadKey] = useState(Date.now());
  
  // Gallery state
  const [currentGalleryImageIndex, setCurrentGalleryImageIndex] = useState(0);
  const [galleryImageFading, setGalleryImageFading] = useState(false);

  // Function to detect browser, OS, and screen info
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';
    
    // Detect browser
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
    
    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'MacOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    return {
      browser,
      os,
      userAgent: ua,
      screenResolution: `${window.screen.width}x${window.screen.height}`
    };
  };

  // Send heartbeat to track active devices
  const sendHeartbeat = async () => {
    try {
      const deviceInfo = getDeviceInfo();
      const formData = new FormData();
      formData.append('screen_resolution', deviceInfo.screenResolution);
      formData.append('browser', deviceInfo.browser);
      formData.append('os', deviceInfo.os);
      formData.append('user_agent', deviceInfo.userAgent);
      
      await axios.post(`${API}/display/${qrCodeId}/heartbeat`, formData, {
        headers: getApiHeaders()
      });
    } catch (error) {
      // Silent fail - don't disrupt display if heartbeat fails
      console.error('Heartbeat error:', error);
    }
  };

  // Helper to convert authenticated file URLs to public URLs for display screens
  const getPublicImageUrl = (url) => {
    if (!url) return '';
    // If it's a full URL (Cloudinary), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Convert /api/files/ to /api/public-files/ for public access
    if (url.startsWith('/api/files/')) {
      return `${BACKEND_URL}${url.replace('/api/files/', '/api/public-files/')}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  // Calculate how many messages can fit based on screen height and orientation
  useEffect(() => {
    const calculateMessagesPerPage = () => {
      if (!data || !data.screen) {
        setMessagesPerPage(9); // Default for landscape
        return;
      }

      const isPortrait = data.screen.orientation === 'portrait';
      
      if (isPortrait) {
        // Portrait: Use dynamic calculation based on screen height
        const screenHeight = window.innerHeight;
        const headerHeight = document.querySelector('.display-header')?.offsetHeight || 0;
        const bylineHeight = 40;
        const screenNameHeight = 80;
        const messageCardHeight = 140;
        
        const availableHeight = screenHeight - headerHeight - bylineHeight - screenNameHeight;
        const calculatedMessages = Math.floor(availableHeight / messageCardHeight);
        
        // Set minimum of 4 messages and maximum of 12 for portrait
        const finalMessagesCount = Math.max(4, Math.min(12, calculatedMessages));
        setMessagesPerPage(finalMessagesCount);
      } else {
        // Landscape: 3 columns × 3 rows = 9 messages per page
        const columns = data.organization?.message_columns_landscape || 3;
        const rows = 3;
        setMessagesPerPage(columns * rows);
      }
    };

    calculateMessagesPerPage();
    
    // Recalculate on window resize
    const handleResize = () => {
      calculateMessagesPerPage();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data]);

  // Smart polling system with adaptive intervals
  useEffect(() => {
    fetchData();
    fetchWeather();
    sendHeartbeat(); // Send initial heartbeat
    
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    let pollInterval = 5000; // Poll every 5 seconds for faster content updates
    let lastSettingsVersion = null;
    let lastContentVersion = null;
    let weatherPollCount = 0;
    let isPolling = true;
    
    const smartPoll = async () => {
      if (!isPolling) return;
      
      try {
        // Fetch fresh data
        const response = await axios.get(`${API}/display/${qrCodeId}`, {
          headers: getApiHeaders()
        });
        const newData = response.data;
        
        const currentSettingsVersion = newData.organization.settings_version;
        const currentContentVersion = newData.organization.content_version;
        
        // Detect settings change (screensaver, theme, etc.)
        if (lastSettingsVersion !== null && currentSettingsVersion !== lastSettingsVersion) {
          console.log('[Settings Changed] Reloading page...', { from: lastSettingsVersion, to: currentSettingsVersion });
          window.location.reload(true); // Hard reload to bypass cache
          return;
        }
        
        // Detect content change (posters added/removed/changed, playlist activated/deactivated)
        if (lastContentVersion !== null && currentContentVersion !== lastContentVersion) {
          console.log('[Content Changed] Reloading page...', { from: lastContentVersion, to: currentContentVersion });
          window.location.reload(true); // Hard reload to bypass cache
          return;
        }
        
        lastSettingsVersion = currentSettingsVersion;
        lastContentVersion = currentContentVersion;
        setSettingsVersion(currentSettingsVersion);
        setContentVersion(currentContentVersion);
        setData(newData);
        
        // Refresh posters (for new uploads)
        fetchPosters();
        
        // Weather less frequently (every 3rd poll)
        weatherPollCount++;
        if (weatherPollCount >= 3) {
          fetchWeather();
          weatherPollCount = 0;
        }
        
      } catch (error) {
        console.error('[Smart Poll] Error:', error);
      }
      
      // Schedule next poll (recursive setTimeout for consistent intervals)
      if (isPolling) {
        setTimeout(smartPoll, pollInterval);
      }
    };
    
    // Start polling immediately
    const initialTimeout = setTimeout(smartPoll, 1000);

    // Heartbeat every 30 seconds
    const heartbeatInterval = setInterval(() => {
      sendHeartbeat();
    }, 30000);

    // Force check when window regains focus (handles browser cache issues)
    const handleFocus = async () => {
      console.log('[Focus Event] Checking for updates...');
      try {
        const response = await axios.get(`${API}/display/${qrCodeId}`, {
          headers: getApiHeaders()
        });
        const newData = response.data;
        if (lastSettingsVersion !== null && newData.organization.settings_version !== lastSettingsVersion) {
          console.log('[Focus] Settings changed, reloading...');
          window.location.reload(true); // Hard reload
          return;
        }
        if (lastContentVersion !== null && newData.organization.content_version !== lastContentVersion) {
          console.log('[Focus] Content changed, reloading...');
          window.location.reload(true); // Hard reload
          return;
        }
      } catch (error) {
        console.error('[Focus] Error checking for updates:', error);
      }
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      isPolling = false;
      clearInterval(timeInterval);
      clearInterval(heartbeatInterval);
      clearTimeout(initialTimeout);
      window.removeEventListener('focus', handleFocus);
    };
  }, [qrCodeId]);

  // Fetch posters when data is loaded
  useEffect(() => {
    if (data?.screen?.id && data?.organization?.id) {
      fetchPosters();
    }
  }, [data?.screen?.id, data?.organization?.id]);

  // Check if screensaver should be active
  const isScreensaverTime = () => {
    if (!data?.organization?.screensaver_enabled) {
      return false;
    }
    
    // Check if this screen is in the allowed list (empty array = all screens)
    const screensaverScreenIds = data.organization.screensaver_screen_ids || [];
    if (screensaverScreenIds.length > 0 && !screensaverScreenIds.includes(data.screen.id)) {
      return false;
    }
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const startTime = data.organization.screensaver_start_time || '22:00';
    const endTime = data.organization.screensaver_end_time || '07:00';
    
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    
    const start = startH * 60 + startM;
    const end = endH * 60 + endM;
    
    // Handle overnight periods (e.g., 22:00 to 07:00)
    let shouldShow;
    if (start > end) {
      shouldShow = currentTime >= start || currentTime < end;
    } else {
      shouldShow = currentTime >= start && currentTime < end;
    }
    
    return shouldShow;
  };

  // Get screensaver background image URL
  const getScreensaverBackgroundUrl = () => {
    const bgImage = data?.organization?.screensaver_background_image;
    if (!bgImage) return null;
    
    // Handle special default image codes
    // Use same landscape image for all orientations - will be centered and fill height on portrait
    if (bgImage === 'AUTO_DETECT' || bgImage === 'DEFAULT_LANDSCAPE' || bgImage === 'DEFAULT_PORTRAIT') {
      return 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop';
    }
    
    // Regular uploaded image
    return bgImage.startsWith('http') ? bgImage : `${BACKEND_URL}${bgImage}`;
  };

  // Screensaver check interval
  useEffect(() => {
    if (!data?.organization) {
      return;
    }
    
    const checkScreensaver = () => {
      const shouldShow = isScreensaverTime();
      setShowingScreensaver(shouldShow);
    };
    
    checkScreensaver(); // Check immediately
    const interval = setInterval(checkScreensaver, 15000); // Check every 15 seconds for faster response
    
    return () => {
      clearInterval(interval);
    };
  }, [data?.organization]);

  // Bouncing clock animation
  useEffect(() => {
    if (!showingScreensaver) return;
    
    // Initialize position within safe bounds on first render
    const clockSize = 350; // Safe padding (analog: 300px, digital: ~450px width)
    const safeX = Math.max(50, Math.min(window.innerWidth - clockSize, 100));
    const safeY = Math.max(50, Math.min(window.innerHeight - clockSize, 100));
    
    setClockPosition({ x: safeX, y: safeY });
    
    const animateClock = () => {
      setClockPosition(prev => {
        const maxX = window.innerWidth - clockSize;
        const maxY = window.innerHeight - clockSize;
        
        let newX = prev.x + clockVelocity.x;
        let newY = prev.y + clockVelocity.y;
        let newVelX = clockVelocity.x;
        let newVelY = clockVelocity.y;
        
        // Bounce on edges with proper boundary clamping
        if (newX <= 0) {
          newVelX = Math.abs(newVelX); // Ensure positive velocity
          newX = 0;
        } else if (newX >= maxX) {
          newVelX = -Math.abs(newVelX); // Ensure negative velocity
          newX = maxX;
        }
        
        if (newY <= 0) {
          newVelY = Math.abs(newVelY);
          newY = 0;
        } else if (newY >= maxY) {
          newVelY = -Math.abs(newVelY);
          newY = maxY;
        }
        
        // Update velocity if it changed
        if (newVelX !== clockVelocity.x || newVelY !== clockVelocity.y) {
          setClockVelocity({ x: newVelX, y: newVelY });
        }
        
        return { x: newX, y: newY };
      });
    };
    
    const interval = setInterval(animateClock, 50); // 20 FPS
    return () => clearInterval(interval);
  }, [showingScreensaver, clockVelocity]);

  // iFrame reload timer - reload iframe based on its reload_interval setting
  useEffect(() => {
    if (!showingPoster || !currentPoster || currentPoster.poster_type !== 'iframe') {
      return;
    }
    
    const reloadInterval = (currentPoster.iframe_reload_interval || 5) * 60 * 1000; // Convert minutes to ms
    console.log(`[iFrame] Setting reload timer for ${currentPoster.iframe_reload_interval || 5} minutes`);
    
    const timer = setInterval(() => {
      console.log('[iFrame] Reloading iframe content');
      setIframeReloadKey(Date.now());
    }, reloadInterval);
    
    return () => clearInterval(timer);
  }, [showingPoster, currentPoster]);

  // Gallery image rotation timer
  useEffect(() => {
    if (!showingPoster || !currentPoster || currentPoster.poster_type !== 'gallery') {
      return;
    }
    
    const images = currentPoster.gallery_images || [];
    if (images.length === 0) return;
    if (images.length <= 1) {
      console.log('[Gallery] Only 1 image, no rotation needed');
      return;
    }
    
    // Reset to first image when poster changes
    setCurrentGalleryImageIndex(0);
    
    const imageDuration = (currentPoster.gallery_image_duration || 5) * 1000; // Convert to ms
    console.log(`[Gallery] Starting rotation: ${images.length} images, ${imageDuration}ms per image`);
    
    const timer = setInterval(() => {
      setCurrentGalleryImageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % images.length;
        console.log(`[Gallery] Rotating from image ${prevIndex} to ${nextIndex}`);
        return nextIndex;
      });
    }, imageDuration);
    
    return () => {
      console.log('[Gallery] Stopping rotation');
      clearInterval(timer);
    };
  }, [showingPoster, currentPoster?.id, currentPoster?.gallery_image_duration]);

  // Playlist/Poster display logic
  useEffect(() => {
    console.log('[Poster Display] useEffect triggered, posters:', posters?.length);
    
    if (!posters || posters.length === 0) {
      console.log('[Poster Display] No posters to display');
      return;
    }

    const settings = data?.organization?.poster_settings || { pause_interval_seconds: 60 };
    const initialPauseInterval = (settings.pause_interval_seconds || 60) * 1000; // Convert to ms
    console.log('[Poster Display] Starting cycle with', posters.length, 'playlists, initial pause:', initialPauseInterval / 1000, 'seconds');
    
    let timeoutIds = [];

    const clearAllTimeouts = () => {
      timeoutIds.forEach(id => clearTimeout(id));
      timeoutIds = [];
    };

    const showPlaylistCycle = () => {
      if (posters.length === 0) return;

      let playlistIndex = 0;

      const showNextPlaylist = () => {
        if (playlistIndex >= posters.length) {
          playlistIndex = 0;
        }

        const playlist = posters[playlistIndex];
        
        if (playlist.posters.length === 0) {
          playlistIndex++;
          const nextTimeout = setTimeout(showNextPlaylist, 1000);
          timeoutIds.push(nextTimeout);
          return;
        }

        let posterIndexInPlaylist = 0;

        const showNextPosterInPlaylist = () => {
          if (posterIndexInPlaylist >= playlist.posters.length) {
            // All posters in this playlist shown, wait hideAfter then show next playlist
            const hideAfter = (playlist.hideAfter || 300) * 1000;
            
            // Fade out and hide overlay
            setPosterFadeIn(false);
            const fadeOutTimeout = setTimeout(() => {
              console.log('[Poster Display] Hiding poster after', hideAfter / 1000, 'seconds');
              setShowingPoster(false);
              
              // Move to next playlist
              playlistIndex++;
              const nextPlaylistTimeout = setTimeout(showNextPlaylist, hideAfter);
              timeoutIds.push(nextPlaylistTimeout);
            }, 1000);
            timeoutIds.push(fadeOutTimeout);
            return;
          }

          const poster = playlist.posters[posterIndexInPlaylist];
          
          // Set current poster for display
          console.log('[Poster Display] Showing poster:', poster.title, 'type:', poster.poster_type);
          setCurrentPoster(poster);
          setCurrentPosterIndex(posterIndexInPlaylist);
          setShowingPoster(true);
          console.log('[Poster Display] showingPoster set to TRUE');
          
          // Reset iframe reload key when a new poster is shown
          if (poster.poster_type === 'iframe') {
            setIframeReloadKey(Date.now());
          }
          
          // Fade in
          const fadeInTimeout = setTimeout(() => setPosterFadeIn(true), 50);
          timeoutIds.push(fadeInTimeout);

          // Show for duration_seconds
          const duration = (poster.duration_seconds || 10) * 1000;
          const durationTimeout = setTimeout(() => {
            // Move to next poster in playlist
            posterIndexInPlaylist++;
            
            // If there are more posters, start fading to next one 1 second earlier
            if (posterIndexInPlaylist < playlist.posters.length) {
              // Start fade out and immediately prepare next poster
              setPosterFadeIn(false);
              const transitionTimeout = setTimeout(() => {
                showNextPosterInPlaylist();
              }, 0); // Immediate transition to avoid message screen flash
              timeoutIds.push(transitionTimeout);
            } else {
              // Last poster in playlist, proceed to hide overlay
              showNextPosterInPlaylist();
            }
          }, duration - 1000); // Subtract 1 second to start transition earlier
          timeoutIds.push(durationTimeout);
        };

        showNextPosterInPlaylist();
      };

      showNextPlaylist();
    };

    // Start first cycle after initial pause
    const initialTimeout = setTimeout(showPlaylistCycle, initialPauseInterval);
    timeoutIds.push(initialTimeout);

    return () => {
      console.log('[Poster Display] Cleaning up');
      clearAllTimeouts();
    };
  }, [posters?.length, data?.organization?.poster_settings?.pause_interval_seconds]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/display/${qrCodeId}`, {
        headers: getApiHeaders()
      });
      const newData = response.data;
      
      // Check if settings version changed (reload page for instant updates)
      if (settingsVersion !== null && newData.organization.settings_version !== settingsVersion) {
        console.log('[fetchData] Settings changed, reloading...', { from: settingsVersion, to: newData.organization.settings_version });
        window.location.reload(true); // Hard reload to bypass cache
        return;
      }
      
      // Check if content version changed (posters added/removed/changed)
      if (contentVersion !== null && newData.organization.content_version !== contentVersion) {
        console.log('[fetchData] Content changed, reloading...', { from: contentVersion, to: newData.organization.content_version });
        window.location.reload(true); // Hard reload to bypass cache
        return;
      }
      
      setSettingsVersion(newData.organization.settings_version);
      setContentVersion(newData.organization.content_version);
      setData(newData);
      setDemoExpired(false); // Reset demo expired state on successful fetch
    } catch (error) {
      console.error('Error fetching display data:', error);
      
      // Check if demo has expired (410 Gone response)
      if (error.response?.status === 410) {
        const errorDetail = error.response.data?.detail;
        if (errorDetail?.error === 'demo_expired') {
          setDemoExpired(true);
          setExpiredOrgName(errorDetail.organization_name || 'Organisasjonen');
          console.log('[fetchData] Demo expired for:', errorDetail.organization_name);
        }
      }
    }
  };

  // Helper function to get translated text
  const t = (key, defaultText) => {
    // Simple translations for display screen
    const lang = data?.organization?.language || 'no';
    const displayTranslations = {
      'informs_about': {
        'no': 'informerer om',
        'en': 'informs about'
      }
    };
    
    if (displayTranslations[key] && displayTranslations[key][lang]) {
      return displayTranslations[key][lang];
    }
    
    return defaultText;
  };

  const fetchWeather = async () => {
    try {
      if (!data?.organization?.weather_location) return;
      const response = await axios.get(`${API}/weather/${data.organization.weather_location}`, {
        headers: getApiHeaders()
      });
      setWeather(response.data);
    } catch (error) {
      console.error('Error fetching weather:', error);
    }
  };

  const fetchPosters = async () => {
    try {
      if (!data?.screen?.id) {
        console.log('[Posters] No screen ID, skipping fetch');
        return;
      }
      
      console.log('[Posters] Fetching for screen:', data.screen.id);
      const response = await axios.get(`${API}/public-display/playlists/${data.organization.id}/${data.screen.id}`, {
        headers: getApiHeaders()
      });
      console.log('[Posters] Response:', response.data.length, 'playlists');
      
      // Flatten all posters from all playlists for display
      const playlistData = response.data.map(playlist => ({
        playlistId: playlist.id,
        playlistTitle: playlist.title,
        hideAfter: playlist.hide_duration,
        posters: playlist.posters || []
      }));
      
      setPosters(playlistData);
      console.log('[Posters] Set posters state:', playlistData.length, 'playlists');
    } catch (error) {
      console.error('[Posters] Error fetching:', error);
    }
  };

  useEffect(() => {
    if (!data) return;
    
    const imagesLength = data.images?.length || 0;
    const messagesLength = data.messages?.length || 0;
    const articlesLength = data.articles?.filter(article => article.is_visible !== false).length || 0;
    const rotationSpeed = (data.organization?.rotation_speed || 8) * 1000;
    const totalPages = Math.ceil(messagesLength / messagesPerPage) || 1;
    
    console.log('[Rotation Setup]', {
      imagesLength,
      messagesLength,
      articlesLength,
      messagesPerPage,
      totalPages,
      rotationSpeed: rotationSpeed / 1000
    });
    
    const imageInterval = setInterval(() => {
      if (imagesLength > 0) {
        setCurrentImageIndex(prev => (prev + 1) % imagesLength);
      }
    }, 5000);

    const articleInterval = setInterval(() => {
      if (articlesLength > 0) {
        setCurrentArticleIndex(prev => (prev + 1) % articlesLength);
      }
    }, rotationSpeed);

    const messageInterval = setInterval(() => {
      if (totalPages > 1) {
        setCurrentMessagePage(prev => {
          const newPage = (prev + 1) % totalPages;
          console.log('[Message Pagination] Page change:', prev, '->', newPage, '/', totalPages);
          return newPage;
        });
      }
    }, rotationSpeed);

    return () => {
      console.log('[Rotation] Cleaning up intervals');
      clearInterval(imageInterval);
      clearInterval(articleInterval);
      clearInterval(messageInterval);
    };
  }, [data, messagesPerPage]);

  useEffect(() => {
    if (data?.organization?.weather_location) {
      fetchWeather();
      const weatherInterval = setInterval(() => {
        fetchWeather();
      }, 3600000); // Update every hour (3600000ms = 1 hour)
      return () => clearInterval(weatherInterval);
    }
  }, [data?.organization?.weather_location]);

  const getDayName = (dateString, index) => {
    if (index === 0) return 'I dag';
    const date = new Date(dateString);
    const days = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];
    return days[date.getDay()];
  };

  // Group weather forecast by unique days (take first forecast of each day)
  const getUniqueDays = (forecast, maxDays = 4) => {
    if (!forecast || forecast.length === 0) return [];
    const uniqueDays = [];
    const seenDates = new Set();
    
    for (const day of forecast) {
      if (!day.time) continue; // Skip entries without time
      const dateKey = new Date(day.time).toDateString();
      if (!seenDates.has(dateKey)) {
        seenDates.add(dateKey);
        uniqueDays.push(day);
        if (uniqueDays.length >= maxDays) break;
      }
    }
    return uniqueDays;
  };

  // Demo expired page - friendly branded message
  if (demoExpired) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#ffffff'
      }}>
        {/* Logo */}
        <div style={{
          marginBottom: '3rem',
          textAlign: 'center'
        }}>
          <img 
            src="/infovegg-logo-inverted.svg" 
            alt="Infovegg" 
            style={{
              height: '80px',
              width: 'auto',
              marginBottom: '0.5rem'
            }}
          />
        </div>

        {/* Main Message */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '24px',
          padding: '3rem',
          maxWidth: '600px',
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '600',
            marginBottom: '1rem',
            color: '#50E3C2'
          }}>
            Demoen har utløpt
          </h2>
          
          <p style={{
            fontSize: '1.2rem',
            lineHeight: '1.7',
            opacity: 0.9,
            marginBottom: '2rem'
          }}>
            Demotiden for <strong style={{ color: '#50E3C2' }}>{expiredOrgName}</strong> er nå over. 
            Takk for at dere prøvde Infovegg!
          </p>

          <p style={{
            fontSize: '1.1rem',
            lineHeight: '1.6',
            opacity: 0.8,
            marginBottom: '2.5rem'
          }}>
            Ønsker dere å fortsette? Alt innholdet deres er trygt lagret, 
            og dere kan fortsette akkurat der dere slapp ved å aktivere et abonnement.
          </p>

          {/* Call to Action */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center'
          }}>
            <a 
              href="https://infovegg.no" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: 'linear-gradient(135deg, #50E3C2 0%, #3dd4b0 100%)',
                color: '#1a1f2e',
                padding: '1rem 2.5rem',
                borderRadius: '12px',
                fontSize: '1.1rem',
                fontWeight: '600',
                textDecoration: 'none',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 8px 24px rgba(80, 227, 194, 0.25)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(80, 227, 194, 0.35)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(80, 227, 194, 0.25)';
              }}
            >
              Besøk infovegg.no
              <span style={{ fontSize: '1.25rem' }}>→</span>
            </a>

            <p style={{
              fontSize: '0.95rem',
              opacity: 0.6,
              margin: '1rem 0 0'
            }}>
              Kontakt oss på <a href="mailto:yngve@infovegg.no" style={{ color: '#50E3C2' }}>yngve@infovegg.no</a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p style={{
          marginTop: '3rem',
          fontSize: '0.9rem',
          opacity: 0.5
        }}>
          Digital informasjonstavle for borettslag og sameier
        </p>
      </div>
    );
  }

  if (!data) {
    return <div className="display-screen dark-mode">Laster...</div>;
  }

  const themeMode = data.organization.dark_mode ? 'dark-mode' : 'light-mode';
  const currentMessages = data.messages.slice(currentMessagePage * messagesPerPage, (currentMessagePage + 1) * messagesPerPage);
  const totalPages = Math.ceil(data.messages.length / messagesPerPage);
  
  // Filter visible articles only
  const visibleArticles = data.articles.filter(article => article.is_visible !== false);
  const hasVisibleArticles = visibleArticles.length > 0;
  
  // Check if all header elements are hidden (for portrait use portrait-specific settings)
  const allHeaderHidden = data.screen.orientation === 'portrait'
    ? (data.organization.show_organization_name_portrait === false && 
       data.organization.show_address_portrait === false && 
       data.organization.show_weather_portrait === false && 
       data.organization.show_clock_portrait === false)
    : (data.organization.show_organization_name === false && 
       data.organization.show_address === false && 
       data.organization.show_weather === false && 
       data.organization.show_clock === false);

  // Render portrait layout
  if (data.screen.orientation === 'portrait') {
    return (
      <div className={`display-screen ${themeMode}`} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Header for Portrait */}
        {!allHeaderHidden && (
          <div style={{ 
            background: themeMode === 'dark-mode' ? '#2A2A2A' : '#FFFFFF',
            padding: '1rem 2rem',
            borderBottom: `2px solid ${data.organization.theme_colors?.primary_color || '#50E3C2'}`
          }}>
            {/* Line 1: Organization name + address (left), Screen name (center), Clock (right) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: data.organization.show_weather_portrait !== false ? '1rem' : 0 }}>
              <div style={{ flex: 1 }}>
                {data.organization.show_organization_name_portrait !== false && (
                  <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>
                    {data.organization.name}
                  </h2>
                )}
                {data.organization.show_address_portrait !== false && data.organization.address && (
                  <p style={{ margin: '0.2rem 0 0 0', fontSize: '1rem', opacity: 0.8 }}>
                    {data.organization.address}
                  </p>
                )}
              </div>
              
              {/* Screen name centered - 15% smaller */}
              {(() => {
                const shouldShow = data.screen.screen_name && data.organization.show_screen_name_portrait !== false;
                console.log('[DisplayScreen Portrait] Screen name check:', {
                  screenName: data.screen.screen_name,
                  showScreenNamePortrait: data.organization.show_screen_name_portrait,
                  shouldShow: shouldShow
                });
                return shouldShow;
              })() && (
                <div style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '2.125rem',
                  fontFamily: "'Orbitron', sans-serif",
                  fontWeight: 'bold',
                  color: data.organization.theme_colors?.primary_color || '#50E3C2',
                  letterSpacing: '0.05em'
                }}>
                  {data.screen.screen_name}
                </div>
              )}
              
              {data.organization.show_clock_portrait !== false && (
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <div className="clock" data-testid="clock" style={{ fontSize: '2.6rem' }}>
                    {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }).split(':')[0]}
                    <span className="colon">:</span>
                    {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }).split(':')[1]}
                  </div>
                </div>
              )}
            </div>
            
            {/* Line 2: Weather centered - 7 days with smaller icons */}
            {data.organization.show_weather_portrait === true && weather && weather.forecast && weather.forecast.length > 0 && (
              <div className="weather-widget" data-testid="weather-widget" style={{ 
                marginTop: '1rem',
                justifyContent: 'space-between',
                width: '100%'
              }}>
                {getUniqueDays(weather.forecast, 7).map((day, idx) => (
                  <div key={idx} className="weather-day">
                    <div className="weather-info">
                      <div className="day-name">{getDayName(day.time, idx)}</div>
                      <div className="temp">{day.temperature ? Math.round(day.temperature) : 'N/A'}°</div>
                    </div>
                    <div className="icon" style={{ 
                      color: data.organization.theme_colors?.primary_color || '#50E3C2'
                    }}>
                      {getWeatherIcon(day.symbol, '2.1rem')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Main Content Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '2rem' }}>
          {/* Messages Section - Pinned full width, others 2 columns */}
          <div style={{ 
            marginBottom: '2rem',
            flex: 1,
            overflowY: 'auto'
          }}>
            {/* Pinned messages - full width */}
            {currentMessages.filter(m => m.is_sticky).map((message, idx) => {
              const isPortrait = data.screen.orientation === 'portrait';
              const titleFontSize = isPortrait 
                ? (data.organization.message_title_font_size_portrait || data.organization.message_title_font_size || '1.375rem')
                : (data.organization.message_title_font_size || '1.375rem');
              const contentFontSize = isPortrait
                ? (data.organization.message_content_font_size_portrait || data.organization.message_content_font_size || '1.375rem')
                : (data.organization.message_content_font_size || '1.375rem');
              
              return (
              <div
                key={`sticky-${idx}`}
                className="message-card sticky"
                style={{ 
                  marginBottom: '1rem',
                  background: data.organization.theme_colors?.primary_color || '#50E3C2',
                  color: data.organization.theme_colors?.pinned_font || '#2A2A2A',
                  borderBottom: `6px solid ${data.organization.theme_colors?.primary_color || '#50E3C2'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h3 style={{ 
                    color: data.organization.theme_colors?.pinned_font || '#2A2A2A',
                    fontSize: titleFontSize,
                    margin: 0,
                    flex: 1
                  }}>
                    {message.title}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem' }}>
                    {message.tag && (() => {
                      const tagConfig = data.organization.message_tags?.find(t => t.name === message.tag);
                      return tagConfig ? (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          fontSize: '0.7rem',
                          borderRadius: '4px',
                          background: tagConfig.color,
                          color: tagConfig.text_color,
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {message.tag}
                        </span>
                      ) : null;
                    })()}
                    <span className="sticky-icon" style={{ color: '#000000' }}>
                      <Lock size={18} />
                    </span>
                  </div>
                </div>
                <p style={{ 
                  color: data.organization.theme_colors?.pinned_font || '#2A2A2A',
                  fontSize: contentFontSize
                }}>
                  {message.message}
                </p>
                <div className="date-line">
                  <span className="date">{new Date(message.created_at).toLocaleDateString('no-NO')}</span>
                </div>
              </div>
            );
            })}
            
            {/* Regular messages - dynamic columns */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${data.organization.message_columns_portrait || 2}, 1fr)`, 
              gap: '1rem',
              alignContent: 'start'
            }}>
              {currentMessages.filter(m => !m.is_sticky).map((message, idx) => {
                const isPortrait = data.screen.orientation === 'portrait';
                const titleFontSize = isPortrait 
                  ? (data.organization.message_title_font_size_portrait || data.organization.message_title_font_size || '1.375rem')
                  : (data.organization.message_title_font_size || '1.375rem');
                const contentFontSize = isPortrait
                  ? (data.organization.message_content_font_size_portrait || data.organization.message_content_font_size || '1.375rem')
                  : (data.organization.message_content_font_size || '1.375rem');
                
                // Determine background based on type
                let messageBackground;
                if (data.organization.message_bg_type === 'solid') {
                  messageBackground = data.organization.message_bg_color_solid || 'rgba(255, 255, 255, 0.4)';
                } else {
                  messageBackground = `linear-gradient(135deg, ${data.organization.message_bg_color_start || '#364f4b'} 0%, ${data.organization.message_bg_color_end || 'rgba(255, 255, 255, 0.44)'} 100%)`;
                }
                
                return (
                <div
                  key={`regular-${idx}`}
                  className="message-card"
                  style={{
                    borderBottom: `6px solid ${data.organization.theme_colors?.primary_color || '#50E3C2'}`,
                    background: messageBackground
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: titleFontSize, margin: 0, flex: 1 }}>
                      {message.title}
                    </h3>
                    {message.tag && (() => {
                      const tagConfig = data.organization.message_tags?.find(t => t.name === message.tag);
                      return tagConfig ? (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          fontSize: '0.7rem',
                          borderRadius: '10px',
                          background: tagConfig.color,
                          color: tagConfig.text_color,
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          marginLeft: '0.5rem'
                        }}>
                          {message.tag}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <p style={{ fontSize: contentFontSize }}>
                    {message.message}
                  </p>
                  <div className="date-line">
                    <span className="date">{new Date(message.created_at).toLocaleDateString('no-NO')}</span>
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* Sticky bottom section for Articles and QR */}
          <div style={{ marginTop: 'auto' }}>

          {/* Articles Section - Turquoise main color with dark text */}
          {hasVisibleArticles && (
            <div style={{
              background: data.organization.theme_colors?.primary_color || '#50E3C2',
              padding: '1.5rem',
              marginBottom: '1rem',
              color: '#2a2a2a'
            }}>
              {(data.organization.show_article_header !== false) && (
                <h2 style={{ marginBottom: '1.5rem', textAlign: 'center', color: '#2a2a2a' }}>
                  <span className="org-name-bold">{data.organization.name}</span>
                  {' '}
                  <span className="informerer-light">info</span>
                </h2>
              )}
              
              {/* Article content with image left, text right */}
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                {visibleArticles[currentArticleIndex].image_url && (
                  <div style={{ flexShrink: 0, width: '40%' }}>
                    <img 
                      src={getPublicImageUrl(visibleArticles[currentArticleIndex].image_url)}
                      alt={visibleArticles[currentArticleIndex].title}
                      style={{ 
                        width: '100%', 
                        height: 'auto',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                )}
                
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    marginTop: 0, 
                    fontSize: data.organization.article_title_font_size_portrait || data.organization.article_title_font_size || '1.5rem', 
                    marginBottom: '1rem', 
                    color: '#2a2a2a',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}>
                    {visibleArticles[currentArticleIndex].title}
                  </h3>
                  <p style={{ 
                    fontSize: data.organization.article_content_font_size_portrait || data.organization.article_content_font_size || '1.1rem', 
                    lineHeight: '1.6', 
                    color: '#2a2a2a' 
                  }}>
                    {visibleArticles[currentArticleIndex].content}
                  </p>
                </div>
              </div>
              
              {visibleArticles.length > 1 && (
                <div className="pagination-dots" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  {visibleArticles.map((_, idx) => (
                    <div 
                      key={idx} 
                      className={`dot ${idx === currentArticleIndex ? 'active' : ''}`}
                      style={{ 
                        display: 'inline-block', 
                        margin: '0 0.3rem',
                        background: idx === currentArticleIndex ? (data.organization.theme_colors?.primary_color_dark || '#429C88') : 'rgba(255, 255, 255, 0.5)',
                        border: 'none'
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* QR Code Section - No space to byline */}
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.95)',
            color: '#2A2A2A',
            padding: '0.75rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ flexShrink: 0 }}>
                <QRCodeSVG 
                  value={`${BACKEND_URL.replace('/api', '')}/submit/${data.screen.qr_code_id}`}
                  size={130}
                  level="M"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div className="qr-instruction-wrapper" style={{ 
                  margin: 0,
                  color: '#2A2A2A',
                  fontWeight: '500',
                  lineHeight: '1.4'
                }} dangerouslySetInnerHTML={{ 
                  __html: data.organization.qr_instruction_text || 'Scan QR-koden for å sende melding til skjermen'
                }}>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Byline footer */}
        {data.organization.show_byline_portrait !== false && (
          <div style={{
            padding: '0.5rem',
            fontSize: '0.65rem',
            color: '#FFFFFF',
            opacity: 0.7,
            textAlign: 'center',
            letterSpacing: '0.15em',
            background: themeMode === 'dark-mode' ? '#2A2A2A' : '#f5f5f5',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            INFOVEGG.NO - MADE BY DISPLAYSYSTEM.NO
          </div>
        )}

        {/* Poster Overlay */}
        {showingPoster && currentPoster && !showingScreensaver && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            opacity: posterFadeIn ? 1 : 0,
            transition: 'opacity 1s ease-in-out'
          }}>
            {currentPoster.poster_type === 'image' ? (
              <img
                src={getPublicImageUrl(currentPoster.image_url)}
                alt={currentPoster.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : currentPoster.poster_type === 'video' ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <iframe
                  src={(() => {
                    try {
                      let embedUrl = '';
                      const videoUrl = currentPoster.video_url;
                      
                      if (!videoUrl) {
                        console.error('No video URL provided');
                        return '';
                      }
                      
                      // Check if it's a Vimeo URL
                      const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
                      if (vimeoMatch) {
                        const vimeoId = vimeoMatch[1];
                        const params = new URLSearchParams({
                          autoplay: '1',
                          muted: currentPoster.audio_enabled ? '0' : '1',
                          ...(currentPoster.video_start_time ? { '#t': `${currentPoster.video_start_time}s` } : {})
                        });
                        embedUrl = `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
                      } else {
                        // YouTube URL
                        const baseUrl = videoUrl.replace('watch?v=', 'embed/');
                        const params = new URLSearchParams({
                          autoplay: '1',
                          mute: currentPoster.audio_enabled ? '0' : '1',
                          ...(currentPoster.video_start_time ? { start: currentPoster.video_start_time } : {}),
                          ...(currentPoster.video_end_time ? { end: currentPoster.video_end_time } : {})
                        });
                        embedUrl = `${baseUrl}?${params.toString()}`;
                      }
                      
                      return embedUrl;
                    } catch (error) {
                      console.error('Error generating video embed URL:', error);
                      return '';
                    }
                  })()}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : currentPoster.poster_type === 'iframe' ? (
              <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <iframe
                  key={`iframe-${currentPoster.id}-${iframeReloadKey}`}
                  src={currentPoster.iframe_url}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    border: 'none',
                    overflow: 'hidden',
                    pointerEvents: 'none'
                  }}
                  scrolling="no"
                  sandbox="allow-scripts allow-same-origin"
                  onError={(e) => {
                    console.error('iFrame failed to load:', currentPoster.iframe_url);
                  }}
                />
                {/* Error overlay - shown if iframe fails */}
                <div 
                  id={`iframe-error-${currentPoster.id}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0, 0, 0, 0.9)',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '2rem',
                    textAlign: 'center',
                    padding: '2rem'
                  }}
                >
                  <div>
                    <p>⚠️ Kunne ikke laste innhold</p>
                    <p style={{ fontSize: '1.2rem', marginTop: '1rem', opacity: 0.7 }}>
                      {currentPoster.iframe_url}
                    </p>
                  </div>
                </div>
              </div>
            ) : currentPoster.poster_type === 'gallery' ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                {currentPoster.gallery_images && currentPoster.gallery_images.length > 0 && (
                  <img
                    src={currentPoster.gallery_images[currentGalleryImageIndex]}
                    alt={`${currentPoster.title} - bilde ${currentGalleryImageIndex + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Screensaver Overlay */}
        {showingScreensaver && data && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            overflow: 'hidden',
            background: '#000'
          }}>
            {/* Background Image */}
            {getScreensaverBackgroundUrl() && (
              <img
                src={getScreensaverBackgroundUrl()}
                alt="Screensaver background"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            )}
            
            {/* Dark Overlay */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: data.organization.screensaver_background_color || '#000000',
              opacity: data.organization.screensaver_overlay_opacity || 0.5
            }}></div>

            {/* Bouncing Clock */}
            <div style={{
              position: 'absolute',
              left: `${clockPosition.x}px`,
              top: `${clockPosition.y}px`,
              transition: 'all 0.05s linear',
              color: 'white',
              textAlign: 'center',
              pointerEvents: 'none'
            }}>
              {data.organization.screensaver_clock_type === 'analog' ? (
                // Analog Clock (SVG) - 150% larger
                <svg width="300" height="300" viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="90" fill="rgba(255,255,255,0.1)" stroke="white" strokeWidth="4"/>
                  {/* Hour markers */}
                  {[...Array(12)].map((_, i) => {
                    const angle = (i * 30 - 90) * (Math.PI / 180);
                    const x1 = 100 + 75 * Math.cos(angle);
                    const y1 = 100 + 75 * Math.sin(angle);
                    const x2 = 100 + 85 * Math.cos(angle);
                    const y2 = 100 + 85 * Math.sin(angle);
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="3"/>;
                  })}
                  {/* Hour hand */}
                  <line
                    x1="100"
                    y1="100"
                    x2={100 + 40 * Math.sin((currentTime.getHours() % 12 + currentTime.getMinutes() / 60) * 30 * (Math.PI / 180))}
                    y2={100 - 40 * Math.cos((currentTime.getHours() % 12 + currentTime.getMinutes() / 60) * 30 * (Math.PI / 180))}
                    stroke="white"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                  {/* Minute hand */}
                  <line
                    x1="100"
                    y1="100"
                    x2={100 + 60 * Math.sin(currentTime.getMinutes() * 6 * (Math.PI / 180))}
                    y2={100 - 60 * Math.cos(currentTime.getMinutes() * 6 * (Math.PI / 180))}
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  {/* Second hand */}
                  <line
                    x1="100"
                    y1="100"
                    x2={100 + 70 * Math.sin(currentTime.getSeconds() * 6 * (Math.PI / 180))}
                    y2={100 - 70 * Math.cos(currentTime.getSeconds() * 6 * (Math.PI / 180))}
                    stroke="#50E3C2"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  {/* Center dot */}
                  <circle cx="100" cy="100" r="5" fill="white"/>
                </svg>
              ) : (
                // Digital Clock - 144px (60% larger than original)
                <div style={{
                  fontSize: '144px',
                  fontFamily: "'Orbitron', monospace",
                  fontWeight: 'bold',
                  textShadow: '0 0 20px rgba(80, 227, 194, 0.5), 0 0 40px rgba(80, 227, 194, 0.3)',
                  letterSpacing: '0.1em'
                }}>
                  {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render landscape layout (original layout)
  return (
    <div className={`display-screen ${themeMode}`}>
      {/* Header - only show if at least one element is visible */}
      {!allHeaderHidden && (
      <div className={`display-header ${themeMode}`}>
        {/* Organization info - conditional */}
        {(data.organization.show_organization_name !== false || data.organization.show_address !== false) && (
          <div className="org-info">
            {data.organization.show_organization_name !== false && (
              <h1 data-testid="org-name">{data.organization.name}</h1>
            )}
            {data.organization.show_address !== false && data.organization.address && (
              <p data-testid="org-address">{data.organization.address}</p>
            )}
          </div>
        )}
        
        {/* Weather widget centered - conditional */}
        {data.organization.show_weather !== false && weather && weather.forecast && weather.forecast.length > 0 && (
          <div className="weather-widget" data-testid="weather-widget">
            {getUniqueDays(weather.forecast).map((day, idx) => (
              <div key={idx} className="weather-day">
                <div className="weather-info">
                  <div className="day-name">{getDayName(day.time, idx)}</div>
                  <div className="temp">{day.temperature ? Math.round(day.temperature) : 'N/A'}°</div>
                </div>
                <div className="icon" style={{ color: data.organization.theme_colors?.primary_color || '#50E3C2' }}>
                  {getWeatherIcon(day.symbol)}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Clock on the right - conditional */}
        {data.organization.show_clock !== false && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div className="clock" data-testid="clock">
              {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }).split(':')[0]}
              <span className="colon">:</span>
              {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }).split(':')[1]}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Main Content */}
      <div className={`display-content ${data.screen.orientation} ${allHeaderHidden ? 'no-header' : ''}`}>
        {/* LEFT SIDEBAR: Image + Article + QR Code */}
        <div className="left-sidebar">
          {/* Image Section - TOP (optional) */}
          {(data.organization.show_image_banner === true || data.organization.show_image_banner === null || data.organization.show_image_banner === undefined) && (
            data.images.length > 0 ? (
              <div className="image-section" data-testid="image-section">
                <img
                  src={getPublicImageUrl(data.images[currentImageIndex].image_url)}
                  alt="Info"
                />
                {data.images.length > 1 && (
                  <div className="pagination-dots">
                    {data.images.map((_, idx) => (
                      <div key={idx} className={`dot ${idx === currentImageIndex ? 'active' : ''}`} />
                    ))}
                  </div>
                )}
              </div>
            ) : data.organization.default_image_url && (
              <div className="image-section" data-testid="image-section">
                <img
                  src={data.organization.default_image_url}
                  alt="Info"
                />
              </div>
            )
          )}

          {/* Articles Section */}
          {hasVisibleArticles && (
            <div 
              className="articles-section" 
              data-testid="articles-section"
              style={{ background: data.organization.theme_colors?.primary_color || '#50E3C2' }}
            >
              {(data.organization.show_article_header_portrait !== false) && (
                <h2>
                  <span className="org-name-bold" style={{ color: '#ffffff' }}>{data.organization.name}</span>
                  {' '}
                  <span className="informerer-light" style={{ color: '#ffffff' }}>info</span>
                </h2>
              )}
              <div className="article-content">
                <h3 style={{ fontSize: data.screen.orientation === 'portrait' 
                  ? (data.organization.article_title_font_size_portrait || data.organization.article_title_font_size || '1.5rem')
                  : (data.organization.article_title_font_size || '1.5rem') 
                }}>
                  {visibleArticles[currentArticleIndex].title}
                </h3>
                {visibleArticles[currentArticleIndex].image_url && (
                  <div style={{ 
                    width: '100%', 
                    marginBottom: '1rem'
                  }}>
                    <img 
                      src={getPublicImageUrl(visibleArticles[currentArticleIndex].image_url)}
                      alt={visibleArticles[currentArticleIndex].title}
                      style={{ 
                        width: '100%', 
                        height: 'auto',
                        display: 'block',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
                <p style={{ fontSize: data.screen.orientation === 'portrait'
                  ? (data.organization.article_content_font_size_portrait || data.organization.article_content_font_size || '1.1rem')
                  : (data.organization.article_content_font_size || '1.1rem')
                }}>
                  {visibleArticles[currentArticleIndex].content}
                </p>
              </div>
              {visibleArticles.length > 1 && (
                <div className="pagination-dots">
                  {visibleArticles.map((_, idx) => (
                    <div 
                      key={idx} 
                      className={`dot ${idx === currentArticleIndex ? 'active' : ''}`}
                      style={{
                        background: idx === currentArticleIndex ? (data.organization.theme_colors?.primary_color_dark || '#429C88') : 'rgba(255, 255, 255, 0.5)',
                        border: 'none'
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* QR Code Section - BOTTOM OF LEFT SIDEBAR */}
          <div className="qr-section-bottom" data-testid="qr-section" style={{ 
            marginTop: 'auto',
            padding: '0.75rem',
            paddingBottom: '3rem',
            background: 'rgba(255, 255, 255, 0.95)',
            color: '#2A2A2A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
              <div className="qr-code-container">
                <QRCodeSVG 
                  value={`${BACKEND_URL.replace('/api', '')}/submit/${data.screen.qr_code_id}`}
                  size={140}
                  level="M"
                />
              </div>
              <div className="qr-text" style={{ flex: 1 }}>
                <div className="qr-instruction-wrapper" style={{ 
                  margin: 0,
                  color: '#2A2A2A',
                  fontWeight: '500',
                  lineHeight: '1.4'
                }} dangerouslySetInnerHTML={{ 
                  __html: data.organization.qr_instruction_text || 'Scan QR-koden for å sende melding til skjermen'
                }}>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Messages with vertical pagination */}
        <div className="right-content" style={{ position: 'relative' }}>
          {/* Messages Section */}
          <div className="messages-section" style={{ paddingLeft: '57px', paddingRight: '57px' }}>
            {/* Screen Location Name */}
            {(() => {
              const shouldShow = data.screen.screen_name && data.organization.show_screen_name !== false;
              console.log('[DisplayScreen Landscape] Screen name check:', {
                screenName: data.screen.screen_name,
                showScreenName: data.organization.show_screen_name,
                shouldShow: shouldShow
              });
              return shouldShow;
            })() && (
              <div style={{
                fontSize: '2.5rem',
                fontFamily: "'Orbitron', sans-serif",
                fontWeight: 'bold',
                color: data.organization.theme_colors?.primary_color || '#50E3C2',
                textAlign: 'center',
                marginBottom: '1.5rem',
                padding: '0.75rem',
                background: `${data.organization.theme_colors?.primary_color || '#50E3C2'}1A`,
                letterSpacing: '0.05em'
              }}>
                {data.screen.screen_name}
              </div>
            )}
            
            {/* Pinned messages - full width */}
            {currentMessages.filter(m => m.is_sticky).map((message, idx) => {
              const isPortrait = data.screen.orientation === 'portrait';
              const titleFontSize = isPortrait 
                ? (data.organization.message_title_font_size_portrait || data.organization.message_title_font_size || '1.375rem')
                : (data.organization.message_title_font_size || '1.375rem');
              const contentFontSize = isPortrait
                ? (data.organization.message_content_font_size_portrait || data.organization.message_content_font_size || '1.375rem')
                : (data.organization.message_content_font_size || '1.375rem');
              
              return (
              <div
                key={`sticky-${idx}`}
                className="message-card sticky"
                style={{ 
                  marginBottom: '1rem',
                  background: data.organization.theme_colors?.primary_color || '#50E3C2',
                  color: data.organization.theme_colors?.pinned_font || '#2A2A2A',
                  borderBottom: `6px solid ${data.organization.theme_colors?.primary_color || '#50E3C2'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h3 style={{ 
                    color: data.organization.theme_colors?.pinned_font || '#2A2A2A',
                    fontSize: titleFontSize,
                    margin: 0,
                    flex: 1
                  }}>
                    {message.title}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem' }}>
                    {message.tag && (() => {
                      const tagConfig = data.organization.message_tags?.find(t => t.name === message.tag);
                      return tagConfig ? (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          fontSize: '0.7rem',
                          borderRadius: '4px',
                          background: tagConfig.color,
                          color: tagConfig.text_color,
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {message.tag}
                        </span>
                      ) : null;
                    })()}
                    <span className="sticky-icon" style={{ color: '#000000' }}>
                      <Lock size={18} />
                    </span>
                  </div>
                </div>
                <p style={{ 
                  color: data.organization.theme_colors?.pinned_font || '#2A2A2A',
                  fontSize: contentFontSize
                }}>
                  {message.message}
                </p>
                <div className="date-line">
                  <span className="date">{new Date(message.created_at).toLocaleDateString('no-NO')}</span>
                </div>
              </div>
            );
            })}
            
            {/* Regular messages - dynamic columns */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${data.organization.message_columns_landscape || 3}, 1fr)`, 
              gap: '1rem',
              alignContent: 'start'
            }}>
              {currentMessages.filter(m => !m.is_sticky).map((message, idx) => {
                const isPortrait = data.screen.orientation === 'portrait';
                const titleFontSize = isPortrait 
                  ? (data.organization.message_title_font_size_portrait || data.organization.message_title_font_size || '1.375rem')
                  : (data.organization.message_title_font_size || '1.375rem');
                const contentFontSize = isPortrait
                  ? (data.organization.message_content_font_size_portrait || data.organization.message_content_font_size || '1.375rem')
                  : (data.organization.message_content_font_size || '1.375rem');
                
                // Determine background based on type
                let messageBackground;
                if (data.organization.message_bg_type === 'solid') {
                  messageBackground = data.organization.message_bg_color_solid || 'rgba(255, 255, 255, 0.4)';
                } else {
                  messageBackground = `linear-gradient(135deg, ${data.organization.message_bg_color_start || '#364f4b'} 0%, ${data.organization.message_bg_color_end || 'rgba(255, 255, 255, 0.44)'} 100%)`;
                }
                
                return (
                <div
                  key={`regular-${idx}`}
                  className="message-card"
                  style={{
                    background: messageBackground,
                    borderBottom: `6px solid ${data.organization.theme_colors?.primary_color || '#50E3C2'}`
                  }}
                >
                  <h3 style={{ fontSize: titleFontSize }}>
                    {message.title}
                  </h3>
                  <p style={{ fontSize: contentFontSize }}>
                    {message.message}
                  </p>
                  <div className="date-line" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="date">{new Date(message.created_at).toLocaleDateString('no-NO')}</span>
                    {message.tag && (() => {
                      const tagConfig = data.organization.message_tags?.find(t => t.name === message.tag);
                      return tagConfig ? (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          fontSize: '0.7rem',
                          borderRadius: '4px',
                          background: tagConfig.color,
                          color: tagConfig.text_color,
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {message.tag}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* Vertical Pagination Dots on the right side */}
          {totalPages > 1 && (
            <div 
              data-testid="pagination-dots" 
              style={{
                position: 'absolute',
                right: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                alignItems: 'center'
              }}
            >
              {[...Array(totalPages)].map((_, idx) => (
                <div 
                  key={idx} 
                  className={`dot ${idx === currentMessagePage ? 'active' : ''}`}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: idx === currentMessagePage ? (data.organization.theme_colors?.primary_color_dark || '#429C88') : 'rgba(255, 255, 255, 0.3)',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Byline footer */}
      {data.organization.show_byline !== false && (
        <div style={{
          position: 'absolute',
          bottom: '0.5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '0.65rem',
          color: '#FFFFFF',
          opacity: 0.7,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          letterSpacing: '0.15em',
          zIndex: 10
        }}>
          INFOVEGG.NO - MADE BY DISPLAYSYSTEM.NO
        </div>
      )}

      {/* Poster Overlay */}
      {showingPoster && currentPoster && !showingScreensaver && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          opacity: posterFadeIn ? 1 : 0,
          transition: 'opacity 1s ease-in-out'
        }}>
          {currentPoster.poster_type === 'image' ? (
            <img
              src={getPublicImageUrl(currentPoster.image_url)}
              alt={currentPoster.title}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          ) : currentPoster.poster_type === 'video' ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <iframe
                src={(() => {
                  try {
                    let embedUrl = '';
                    const videoUrl = currentPoster.video_url;
                    
                    if (!videoUrl) {
                      console.error('No video URL provided');
                      return '';
                    }
                    
                    // Check if it's a Vimeo URL
                    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
                    if (vimeoMatch) {
                      const vimeoId = vimeoMatch[1];
                      const params = new URLSearchParams({
                        autoplay: '1',
                        muted: currentPoster.audio_enabled ? '0' : '1',
                        ...(currentPoster.video_start_time ? { '#t': `${currentPoster.video_start_time}s` } : {})
                      });
                      embedUrl = `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
                    } else {
                      // YouTube URL
                      const baseUrl = videoUrl.replace('watch?v=', 'embed/');
                      const params = new URLSearchParams({
                        autoplay: '1',
                        mute: currentPoster.audio_enabled ? '0' : '1',
                        ...(currentPoster.video_start_time ? { start: currentPoster.video_start_time } : {}),
                        ...(currentPoster.video_end_time ? { end: currentPoster.video_end_time } : {})
                      });
                      embedUrl = `${baseUrl}?${params.toString()}`;
                    }
                    
                    return embedUrl;
                  } catch (error) {
                    console.error('Error generating video embed URL:', error);
                    return '';
                  }
                })()}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : currentPoster.poster_type === 'iframe' ? (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              <iframe
                key={`iframe-${currentPoster.id}-${iframeReloadKey}`}
                src={currentPoster.iframe_url}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  border: 'none',
                  overflow: 'hidden',
                  pointerEvents: 'none'
                }}
                scrolling="no"
                sandbox="allow-scripts allow-same-origin"
                onError={(e) => {
                  console.error('iFrame failed to load:', currentPoster.iframe_url);
                }}
              />
              {/* Error overlay - shown if iframe fails */}
              <div 
                id={`iframe-error-${currentPoster.id}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(0, 0, 0, 0.9)',
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '2rem',
                  textAlign: 'center',
                  padding: '2rem'
                }}
              >
                <div>
                  <p>⚠️ Kunne ikke laste innhold</p>
                  <p style={{ fontSize: '1.2rem', marginTop: '1rem', opacity: 0.7 }}>
                    {currentPoster.iframe_url}
                  </p>
                </div>
              </div>
            </div>
          ) : currentPoster.poster_type === 'gallery' ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
              {currentPoster.gallery_images && currentPoster.gallery_images.length > 0 && (
                <img
                  src={currentPoster.gallery_images[currentGalleryImageIndex]}
                  alt={`${currentPoster.title} - bilde ${currentGalleryImageIndex + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Screensaver Overlay */}
      {showingScreensaver && data && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          overflow: 'hidden',
          background: '#000'
        }}>
          {/* Background Image */}
          {getScreensaverBackgroundUrl() && (
            <img
              src={getScreensaverBackgroundUrl()}
              alt="Screensaver background"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          )}
          
          {/* Dark Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: data.organization.screensaver_background_color || '#000000',
            opacity: data.organization.screensaver_overlay_opacity || 0.5
          }}></div>

          {/* Bouncing Clock */}
          <div style={{
            position: 'absolute',
            left: `${clockPosition.x}px`,
            top: `${clockPosition.y}px`,
            transition: 'all 0.05s linear',
            color: 'white',
            textAlign: 'center',
            pointerEvents: 'none'
          }}>
            {data.organization.screensaver_clock_type === 'analog' ? (
              // Analog Clock (SVG) - 150% larger
              <svg width="300" height="300" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="90" fill="rgba(255,255,255,0.1)" stroke="white" strokeWidth="4"/>
                {/* Hour markers */}
                {[...Array(12)].map((_, i) => {
                  const angle = (i * 30 - 90) * (Math.PI / 180);
                  const x1 = 100 + 75 * Math.cos(angle);
                  const y1 = 100 + 75 * Math.sin(angle);
                  const x2 = 100 + 85 * Math.cos(angle);
                  const y2 = 100 + 85 * Math.sin(angle);
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="3"/>;
                })}
                {/* Hour hand */}
                <line
                  x1="100"
                  y1="100"
                  x2={100 + 40 * Math.sin((currentTime.getHours() % 12 + currentTime.getMinutes() / 60) * 30 * (Math.PI / 180))}
                  y2={100 - 40 * Math.cos((currentTime.getHours() % 12 + currentTime.getMinutes() / 60) * 30 * (Math.PI / 180))}
                  stroke="white"
                  strokeWidth="6"
                  strokeLinecap="round"
                />
                {/* Minute hand */}
                <line
                  x1="100"
                  y1="100"
                  x2={100 + 60 * Math.sin(currentTime.getMinutes() * 6 * (Math.PI / 180))}
                  y2={100 - 60 * Math.cos(currentTime.getMinutes() * 6 * (Math.PI / 180))}
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                {/* Second hand */}
                <line
                  x1="100"
                  y1="100"
                  x2={100 + 70 * Math.sin(currentTime.getSeconds() * 6 * (Math.PI / 180))}
                  y2={100 - 70 * Math.cos(currentTime.getSeconds() * 6 * (Math.PI / 180))}
                  stroke="#50E3C2"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                {/* Center dot */}
                <circle cx="100" cy="100" r="5" fill="white"/>
              </svg>
            ) : (
              // Digital Clock - 144px (60% larger than original)
              <div style={{
                fontSize: '144px',
                fontFamily: "'Orbitron', monospace",
                fontWeight: 'bold',
                textShadow: '0 0 20px rgba(80, 227, 194, 0.5), 0 0 40px rgba(80, 227, 194, 0.3)',
                letterSpacing: '0.1em'
              }}>
                {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayScreen;
